/* eslint-disable @vp/swan/enforce-grid-structure */
import React, { useEffect, useMemo, useState } from 'react';

import Slider from 'react-slick';

import { Box, Column, GridContainer, Hidden, Row } from '@vp/swan';
import { useLocalizations } from '@vp/ubik-localizations-provider';

import { CarouselData } from '../domain/CarouselData';
import { Combination } from '../domain/Combination';
import { SlideArray } from '../domain/SlideArray';
import { preloadImages } from '../libs/preloader';

import { Gallery } from './Gallery';
import { Thumbnails } from './thumbnails/Thumbnails';

export interface CarouselProps {
  tenant: string;
  carouselData: CarouselData;
  selectedCombination: Combination;
  zoomContainerId?: string;
  canonicalDomain: string;
  mpvId: string;
  productKey: string;
}

export const Carousel: React.FC<CarouselProps> = (props) => {
  const { t } = useLocalizations();

  const [nav1, setNav1] = useState<Slider | undefined>(undefined);
  const [nav2, setNav2] = useState<Slider | undefined>(undefined);
  const [currentSlide, setCurrentSlide] = useState<number | undefined>(undefined);

  useEffect(() => {
    const brandkitImage = props.carouselData.getBrandkitImageIndex(props.selectedCombination);
    if (brandkitImage !== null) {
      setCurrentSlide(brandkitImage);
      return;
    }
    const documentIndex = props.carouselData.getDocumentImageIndex(props.selectedCombination);
    if (documentIndex !== null) {
      setCurrentSlide(documentIndex);
      return;
    }
    if (props.selectedCombination.isInitial) {
      return;
    }

    const referentialImageIndex = props.carouselData.getReferentialImageIndex(
      props.selectedCombination
    );

    if (referentialImageIndex !== null) {
      setCurrentSlide(referentialImageIndex);
    }
  }, [props.selectedCombination, props.carouselData]);

  useEffect(() => {
    if (currentSlide === undefined) return;
    setCurrentSlide(undefined);
  }, [currentSlide]);

  useEffect(() => {
    preloadImages(props.carouselData.getAllThumbnails());
  }, [props.carouselData]);

  const scenesForCombination = useMemo<SlideArray>(
    () => props.carouselData.getSlidesByCombination(props.selectedCombination),
    [props.selectedCombination, props.carouselData]
  );

  return (
    <Box aria-label={t('carousel-container-label')} role="group">
      <GridContainer>
        <Row paddingBottom={4}>
          <Column span={12}>
            <Gallery
              key={scenesForCombination.all.map((m) => m.galleryId).join('-')}
              nav={nav2}
              setNav={setNav1}
              mediaArray={scenesForCombination}
              currentSlide={currentSlide}
              zoomContainerId={props.zoomContainerId}
              tenant={props.tenant}
              canonicalDomain={props.canonicalDomain}
              infinite={scenesForCombination.moreThanSlidesToShow}
            />
          </Column>
        </Row>
        {scenesForCombination.moreThanSingle && (
          <Hidden xs sm>
            <Row>
              <Column span={12}>
                <Thumbnails
                  key={scenesForCombination.all.map((m) => m.galleryId).join('-')}
                  nav={nav1}
                  setNav={setNav2}
                  slideArray={scenesForCombination}
                  setCurrentSlide={setCurrentSlide}
                  infinite={scenesForCombination.moreThanSlidesToShow}
                  mpvId={props.mpvId}
                  productKey={props.productKey}
                />
              </Column>
            </Row>
          </Hidden>
        )}
      </GridContainer>
    </Box>
  );
};
